/**
 * Sistema de Permissões Granulares (Feature Flags)
 *
 * Este sistema permite controle fino de acesso por módulo, independente do role.
 *
 * Regras de Negócio:
 * - ADMIN: Sempre tem acesso total. As permissões são ignoradas.
 * - CLIENT: Acesso restrito apenas ao dashboard de fidelidade. Nunca pode acessar módulos internos.
 * - COLLABORATOR/GESTOR: Acesso definido pelas flags abaixo.
 */

/**
 * Módulos disponíveis no sistema com controle de acesso granular
 */
export enum Permission {
  /** Dashboard financeiro, Custos Operacionais e Fechamentos */
  FINANCE = 'FINANCE',

  /** Acesso exclusivo à geração de recibos (sem acesso ao resto do módulo financeiro) */
  FINANCE_RECEIPT = 'FINANCE_RECEIPT',

  /** Listagem de automações e logs de execução */
  AUTOMATIONS = 'AUTOMATIONS',

  /** Acesso ao módulo People Core (ponto, ajustes, projetos, métricas pessoais) */
  PEOPLE_CORE = 'PEOPLE_CORE',

  /** Gestão de ponto, ajustes e alocações de outros colaboradores */
  PEOPLE_CORE_MANAGEMENT = 'PEOPLE_CORE_MANAGEMENT',

  /** Gestão de senhas de clientes, links de pagamento, vendas e tokens de onboarding */
  CLIENT_OPERATIONS = 'CLIENT_OPERATIONS',

  /** Monitoramento de grupos e análises diárias da operação WhatsApp */
  WHATSAPP_OPERATIONS = 'WHATSAPP_OPERATIONS',

  /** Acesso à Central de Problemas (Gestão de Incidentes e Advertências) */
  INCIDENT_MANAGEMENT = 'INCIDENT_MANAGEMENT',

  /** Acesso ao módulo Instagram para geração de posts com IA */
  INSTAGRAM = 'INSTAGRAM',

  /** Acesso ao módulo Blog para gestão de artigos SEO gerados por IA */
  BLOG = 'BLOG',

  /** Acesso ao módulo de Feedback e PDI (Plano de Desenvolvimento Individual) */
  FEEDBACK_PDI = 'FEEDBACK_PDI',

  /** Gestão do módulo de Feedback e PDI (criar check-ins, PDIs, etc) */
  FEEDBACK_PDI_MANAGEMENT = 'FEEDBACK_PDI_MANAGEMENT',

  /** Acesso ao módulo de Treinamento de Comunicação com IA */
  TRAINING = 'TRAINING',
}

/**
 * Estrutura do objeto de permissões armazenado no campo JSON do usuário
 */
export interface UserPermissions {
  FINANCE: boolean;
  FINANCE_RECEIPT: boolean;
  AUTOMATIONS: boolean;
  PEOPLE_CORE: boolean;
  PEOPLE_CORE_MANAGEMENT: boolean;
  CLIENT_OPERATIONS: boolean;
  WHATSAPP_OPERATIONS: boolean;
  INCIDENT_MANAGEMENT: boolean;
  INSTAGRAM: boolean;
  BLOG: boolean;
  FEEDBACK_PDI: boolean;
  FEEDBACK_PDI_MANAGEMENT: boolean;
  TRAINING: boolean;
}

/**
 * Mapeamento de descrições amigáveis para cada permissão
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
  [Permission.FINANCE]: 'Módulo Financeiro',
  [Permission.FINANCE_RECEIPT]: 'Geração de Recibos',
  [Permission.AUTOMATIONS]: 'Módulo de Automações',
  [Permission.PEOPLE_CORE]: 'People Core (Dados Pessoais)',
  [Permission.PEOPLE_CORE_MANAGEMENT]: 'People Core (Gestão de RH)',
  [Permission.CLIENT_OPERATIONS]: 'Operações e Ações em Massa',
  [Permission.WHATSAPP_OPERATIONS]: 'Grupos WhatsApp',
  [Permission.INCIDENT_MANAGEMENT]: 'Central de Problemas',
  [Permission.INSTAGRAM]: 'Instagram',
  [Permission.BLOG]: 'Blog',
  [Permission.FEEDBACK_PDI]: 'Feedback e PDI',
  [Permission.FEEDBACK_PDI_MANAGEMENT]: 'Feedback e PDI (Gestão)',
  [Permission.TRAINING]: 'Treinamentos',
};

/**
 * Descrições detalhadas de cada permissão
 */
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  [Permission.FINANCE]: 'Acesso ao dashboard financeiro, vendas, comissões, custos operacionais e fechamentos mensais',
  [Permission.FINANCE_RECEIPT]: 'Acesso exclusivo para criar, visualizar e gerenciar recibos (sem acesso ao resto do módulo financeiro)',
  [Permission.AUTOMATIONS]: 'Visualizar automações, logs de execução e dashboard de automações',
  [Permission.PEOPLE_CORE]: 'Acessar o módulo People Core para registrar o próprio ponto, ver métricas pessoais, ajustes e projetos',
  [Permission.PEOPLE_CORE_MANAGEMENT]: 'Gerenciar ponto, ajustes, métricas e alocações de TODOS os colaboradores (visão de RH)',
  [Permission.CLIENT_OPERATIONS]: 'Executar ações em massa (senhas, mensagens, descrições) e gerenciar links de pagamento',
  [Permission.WHATSAPP_OPERATIONS]: 'Acessar grupos WhatsApp, enviar mensagens e monitorar análises diárias da operação',
  [Permission.INCIDENT_MANAGEMENT]: 'Acessar e gerenciar incidentes, advertências e política disciplinar da equipe',
  [Permission.INSTAGRAM]: 'Gerar posts para Instagram/X com IA (Nexus) baseados em perfis da equipe',
  [Permission.BLOG]: 'Gerenciar artigos SEO do blog gerados automaticamente pelo Nexus às 09:00 diariamente',
  [Permission.FEEDBACK_PDI]: 'Acessar check-ins, PDIs, micro-feedbacks e formulários de preparação',
  [Permission.FEEDBACK_PDI_MANAGEMENT]: 'Criar e gerenciar check-ins, PDIs e micro-feedbacks de outros colaboradores',
  [Permission.TRAINING]: 'Acessar treinamentos de comunicação com IA e visualizar histórico de sessões',
};

/**
 * Verifica se o usuário tem uma permissão específica
 * Leva em conta que ADMIN sempre tem todas as permissões
 */
export function hasPermission(
  userRole: string,
  permissions: UserPermissions | undefined,
  permission: Permission
): boolean {
  // ADMIN sempre tem acesso total
  if (userRole === 'ADMIN') {
    return true;
  }

  // CLIENT nunca tem acesso a módulos internos
  if (userRole === 'CLIENT') {
    return false;
  }

  // COLLABORATOR/GESTOR: verificar a flag específica
  return permissions?.[permission] ?? false;
}

/**
 * Verifica se o usuário tem QUALQUER uma das permissões especificadas
 */
export function hasAnyPermission(
  userRole: string,
  permissions: UserPermissions | undefined,
  requiredPermissions: Permission[]
): boolean {
  // ADMIN sempre tem acesso total
  if (userRole === 'ADMIN') {
    return true;
  }

  // CLIENT nunca tem acesso a módulos internos
  if (userRole === 'CLIENT') {
    return false;
  }

  // COLLABORATOR/GESTOR: verificar se tem pelo menos uma das permissões
  return requiredPermissions.some((perm) => permissions?.[perm] ?? false);
}

/**
 * Verifica se o usuário tem TODAS as permissões especificadas
 */
export function hasAllPermissions(
  userRole: string,
  permissions: UserPermissions | undefined,
  requiredPermissions: Permission[]
): boolean {
  // ADMIN sempre tem acesso total
  if (userRole === 'ADMIN') {
    return true;
  }

  // CLIENT nunca tem acesso a módulos internos
  if (userRole === 'CLIENT') {
    return false;
  }

  // COLLABORATOR/GESTOR: verificar se tem todas as permissões
  return requiredPermissions.every((perm) => permissions?.[perm] ?? false);
}
